import React, { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import {
  ArrowLeft, Users, Dumbbell, Trophy, Calendar, TrendingUp,
  ChevronRight, Activity, Flame, Target, Zap, Layers, Check
} from 'lucide-react';

const TIPO_CONFIG = {
  tren_superior: { label: 'Tren Superior', accent: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  tren_inferior: { label: 'Tren Inferior', accent: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' },
  full_body: { label: 'Full Body', accent: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
};

function formatDate(d) {
  if (!d) return 'Nunca';
  return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

// ===== Simple SVG Bar Chart =====
function BarChart({ data, valueKey, labelKey, color = '#d4af37', height = 120 }) {
  const maxVal = Math.max(...data.map(d => d[valueKey]), 1);
  const barW = Math.floor(100 / data.length);
  return (
    <div data-testid="frequency-chart" style={{ height, display: 'flex', alignItems: 'flex-end', gap: '4px', padding: '0 4px' }}>
      {data.map((d, i) => {
        const h = (d[valueKey] / maxVal) * (height - 24);
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
            {d[valueKey] > 0 && (
              <span style={{ fontSize: '10px', color: '#fff', fontWeight: '700' }}>{d[valueKey]}</span>
            )}
            <div style={{
              width: '100%', maxWidth: '36px', height: Math.max(h, 2), borderRadius: '4px 4px 0 0',
              background: d[valueKey] > 0 ? `linear-gradient(to top, ${color}88, ${color})` : 'rgba(255,255,255,0.05)',
              transition: 'height 0.4s ease'
            }} />
            <span style={{ fontSize: '9px', color: '#666', whiteSpace: 'nowrap' }}>{d[labelKey]}</span>
          </div>
        );
      })}
    </div>
  );
}

// ===== Weight Evolution Line Chart (SVG) =====
function WeightLineChart({ registros, color = '#4caf50', height = 100 }) {
  if (!registros || registros.length < 2) {
    return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: '12px' }}>
      {registros?.length === 1 ? `${registros[0].peso}kg (1 registro)` : 'Sin datos suficientes'}
    </div>;
  }
  const weights = registros.map(r => r.peso);
  const minW = Math.min(...weights) * 0.9;
  const maxW = Math.max(...weights) * 1.1 || 1;
  const w = 280;
  const h = height - 20;
  const points = registros.map((r, i) => ({
    x: (i / (registros.length - 1)) * (w - 20) + 10,
    y: h - ((r.peso - minW) / (maxW - minW)) * (h - 10) + 5,
    peso: r.peso, fecha: r.fecha, isRecord: r.is_record
  }));
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  return (
    <svg viewBox={`0 0 ${w} ${height}`} style={{ width: '100%', height }}>
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
        <line key={i} x1="10" y1={5 + (h - 10) * (1 - pct)} x2={w - 10} y2={5 + (h - 10) * (1 - pct)}
          stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
      ))}
      {/* Line */}
      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Fill */}
      <path d={`${pathD} L${points[points.length-1].x},${h} L${points[0].x},${h} Z`} fill={`${color}15`} />
      {/* Points */}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={p.isRecord ? 4 : 2.5} fill={p.isRecord ? '#d4af37' : color} />
          {(i === 0 || i === points.length - 1 || p.isRecord) && (
            <text x={p.x} y={p.y - 7} textAnchor="middle" fill="#ccc" fontSize="8" fontWeight="600">
              {p.peso}
            </text>
          )}
        </g>
      ))}
      {/* Axis labels */}
      <text x="12" y={height - 2} fill="#666" fontSize="8">{registros[0].fecha}</text>
      <text x={w - 12} y={height - 2} fill="#666" fontSize="8" textAnchor="end">{registros[registros.length - 1].fecha}</text>
    </svg>
  );
}

// ===== CLIENT LIST VIEW =====
function ClientList({ clients, onSelect }) {
  if (clients.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-900/40 rounded-xl border border-gray-700">
        <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400">No hay clientes Plus registrados</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="clients-list">
      {clients.map(c => (
        <button key={c.id} onClick={() => onSelect(c)} data-testid={`client-card-${c.id}`}
          className="w-full flex items-center gap-3 p-4 rounded-xl bg-gray-900/60 border border-gray-800 hover:border-gold/30 transition-all group text-left">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-600 to-blue-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 overflow-hidden">
            {c.foto_perfil ? <img src={c.foto_perfil} alt="" className="w-full h-full object-cover" /> : c.nombre[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white">{c.nombre}</div>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
              <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3" /> {c.total_sesiones} sesiones</span>
              <span className="flex items-center gap-1"><Trophy className="w-3 h-3 text-amber-400" /> {c.total_records} records</span>
              {c.ultimo_entreno && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {c.ultimo_entreno}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              {c.rutinas_activas} rutina{c.rutinas_activas !== 1 ? 's' : ''}
            </span>
            <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-gold transition-colors" />
          </div>
        </button>
      ))}
    </div>
  );
}

// ===== CLIENT DETAIL VIEW =====
function ClientDetail({ clientId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedExercise, setExpandedExercise] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/admin/client-progress/${clientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [clientId, token]);

  if (loading) return (
    <div className="text-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gold mx-auto mb-3" />
      <p className="text-gray-400 text-sm">Cargando progreso...</p>
    </div>
  );

  if (!data) return <div className="text-center py-12 text-red-400">Error cargando datos</div>;

  const { client, stats, frecuencia, ejercicios_progresion, historial, asignaciones } = data;

  return (
    <div data-testid="client-detail">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="text-gray-400 hover:text-white" data-testid="btn-back-progress">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-600 to-blue-500 flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden">
          {client.foto_perfil ? <img src={client.foto_perfil} alt="" className="w-full h-full object-cover" /> : client.nombre[0].toUpperCase()}
        </div>
        <div>
          <h2 className="text-lg font-bold text-white">{client.nombre}</h2>
          <span className="text-xs text-cyan-400">Cliente Plus</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-3 text-center">
          <Dumbbell className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
          <div className="text-xl font-bold text-white">{stats.total_sesiones}</div>
          <div className="text-[10px] text-gray-400">Sesiones</div>
        </div>
        <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-3 text-center">
          <Trophy className="w-4 h-4 text-amber-400 mx-auto mb-1" />
          <div className="text-xl font-bold text-white">{stats.total_records}</div>
          <div className="text-[10px] text-gray-400">Records</div>
        </div>
        <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-3 text-center">
          <Target className="w-4 h-4 text-green-400 mx-auto mb-1" />
          <div className="text-xl font-bold text-white">{stats.rutinas_activas}</div>
          <div className="text-[10px] text-gray-400">Rutinas activas</div>
        </div>
      </div>

      {/* Frequency Chart */}
      <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-gold" /> Frecuencia semanal
        </h3>
        <BarChart data={frecuencia} valueKey="sesiones" labelKey="semana" color="#4caf50" height={120} />
      </div>

      {/* Exercise Progression */}
      {ejercicios_progresion.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-gold" /> Evolucion por ejercicio
          </h3>
          <div className="space-y-2">
            {ejercicios_progresion.map((ej, i) => {
              const isExpanded = expandedExercise === i;
              return (
                <div key={i} className="bg-gray-900/60 border border-gray-700 rounded-xl overflow-hidden">
                  <button onClick={() => setExpandedExercise(isExpanded ? null : i)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-800/40 transition-colors"
                    data-testid={`exercise-${i}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-600 to-emerald-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{ej.nombre}</div>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span>{ej.total_registros} registros</span>
                        {ej.record_peso > 0 && (
                          <span className="text-amber-400 flex items-center gap-0.5">
                            <Trophy className="w-3 h-3" /> {ej.record_peso}kg
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-gray-800">
                      <WeightLineChart registros={ej.registros} color="#4caf50" height={100} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Workout History */}
      {historial.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-gold" /> Historial reciente
          </h3>
          <div className="space-y-2">
            {historial.map((h, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-900/40 rounded-xl border border-gray-800">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white">{h.nombre_rutina || h.tipo}</div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{h.total_ejercicios} ejercicios</span>
                    {h.records_rotos > 0 && <span className="text-amber-400 flex items-center gap-0.5"><Trophy className="w-3 h-3" /> {h.records_rotos} records</span>}
                  </div>
                </div>
                <span className="text-xs text-gray-400">{h.fecha}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Assignments */}
      {asignaciones.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-gold" /> Rutinas asignadas
          </h3>
          <div className="space-y-2">
            {asignaciones.map((a, i) => {
              const tc = TIPO_CONFIG[a.rutina_tipo] || TIPO_CONFIG.full_body;
              return (
                <div key={i} className={`flex items-center gap-3 p-3 bg-gray-900/40 rounded-xl border ${a.estado === 'activa' ? tc.border : 'border-gray-800'}`}>
                  <div className={`w-2 h-2 rounded-full ${a.estado === 'activa' ? 'bg-green-400' : 'bg-gray-600'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white">{a.rutina_nombre}</div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className={tc.accent}>{tc.label}</span>
                      <span>{a.sesiones_completadas} sesiones</span>
                    </div>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${a.estado === 'activa' ? 'bg-green-500/10 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
                    {a.estado}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== MAIN PAGE =====
export default function ProgresoCliente() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const token = localStorage.getItem('token');

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/client-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setClients(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  return (
    <div data-testid="progreso-cliente-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-20">
        {selectedClient ? (
          <ClientDetail
            clientId={selectedClient.id}
            onBack={() => setSelectedClient(null)}
          />
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-6 h-6 text-gold" />
              <div>
                <h1 className="text-xl font-bold text-gold">Progreso de Clientes</h1>
                <p className="text-gray-400 text-xs mt-0.5">Seguimiento de evolucion y frecuencia de tus clientes Plus</p>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gold mx-auto mb-3" />
                <p className="text-gray-400 text-sm">Cargando clientes...</p>
              </div>
            ) : (
              <ClientList clients={clients} onSelect={setSelectedClient} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
